import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Trash2 } from 'lucide-react';

/**
 * Centralized admin tab navigation. Renders the same nav bar on every admin page.
 * Active tab is derived from the current pathname.
 */
const AdminNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeTab = location.pathname === '/admin/users'
    ? 'users'
    : location.pathname === '/admin/grant_access'
    ? 'grant_access'
    : location.pathname === '/admin/synopsys_prompt'
    ? 'synopsys_prompt'
    : location.pathname === '/admin/keyword_prompt'
    ? 'keyword_prompt'
    : location.pathname === '/admin/project_status'
    ? 'project_status'
    : location.pathname === '/admin/email_settings'
    ? 'email_settings'
    : location.pathname === '/admin/announcement_settings'
    ? 'announcement_settings'
    : location.pathname === '/admin/orphaned_entries'
    ? 'orphaned_entries'
    : location.pathname.startsWith('/admin/users/')
    ? 'users'
    : 'admin';

  const tabClass = (tab) =>
    activeTab === tab
      ? 'border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-500'
      : 'border-transparent text-gray-500 dark:text-neutral-400 hover:text-gray-700 dark:hover:text-neutral-200 hover:border-gray-300 dark:hover:border-neutral-600';

  return (
    <div className="mb-6 border-b border-gray-200 dark:border-neutral-700">
      <nav className="flex flex-wrap gap-x-2 gap-y-1" aria-label="Admin tabs">
        <button
          onClick={() => navigate('/admin/users')}
          className={`${tabClass('users')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          User Management
        </button>
        <button
          onClick={() => navigate('/admin/grant_access')}
          className={`${tabClass('grant_access')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <Mail className="w-5 h-5 mr-2" />
          Grant Access
        </button>
        <button
          onClick={() => navigate('/admin/synopsys_prompt')}
          className={`${tabClass('synopsys_prompt')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Synopsis Prompt
        </button>
        <button
          onClick={() => navigate('/admin/keyword_prompt')}
          className={`${tabClass('keyword_prompt')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          Keyword Prompt
        </button>
        <button
          onClick={() => navigate('/admin/project_status')}
          className={`${tabClass('project_status')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
          </svg>
          Project Status
        </button>
        <button
          onClick={() => navigate('/admin/email_settings')}
          className={`${tabClass('email_settings')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Email Settings
        </button>
        <button
          onClick={() => navigate('/admin/announcement_settings')}
          className={`${tabClass('announcement_settings')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13a3 3 0 100-6M12 8c0 1.657-.895 3-2 3s-2-1.343-2-3 1.895-3 2-3 2 1.343 2 3z" />
          </svg>
          Announcement setting
        </button>
        <button
          onClick={() => navigate('/admin/orphaned_entries')}
          className={`${tabClass('orphaned_entries')} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center`}
        >
          <Trash2 className="w-5 h-5 mr-2" />
          Orphaned entries
        </button>
      </nav>
    </div>
  );
};

export default AdminNav;
